import React, { FC } from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '../redux/store';
import Loading from '../components/layout/Loading';

type FeatureFlagGuardOptions = {
	// When true, the flag is treated as enabled unless explicitly set to false (default-ON).
	// When false (the default), the flag is treated as disabled unless explicitly set to
	// true (default-OFF). These mirror the read semantics used across the sidebar/toolbar.
	defaultOn?: boolean;
};

/**
 * Route guard that blocks direct navigation to a feature's URL when its feature flag is
 * disabled. Hiding a menu item only removes the entry point — without this, a user could
 * still reach the page by typing the URL. Wrap the route Component with this guard so the
 * page redirects to /404 when the flag is off.
 *
 * Compose it around withProtected, e.g. withFeatureFlag(withProtected(Page), 'agent-enabled').
 */
export const withFeatureFlag = (C: FC, flagName: string, { defaultOn = false }: FeatureFlagGuardOptions = {}) => {
	const FeatureFlagGuard = (props: any) => {
		const loaded = useAppSelector((state) => state.featureFlags?.loaded);
		const flagValue = useAppSelector((state) => state.featureFlags?.flags?.[flagName]);

		// Wait until flags have resolved before deciding, so a disabled feature never
		// flashes its content and an enabled one never wrongly redirects on first paint.
		if (!loaded) {
			return <Loading />;
		}

		const enabled = defaultOn ? flagValue !== false : flagValue === true;
		if (!enabled) {
			return <Navigate to="/404" replace />;
		}

		return <C {...props} />;
	};
	return FeatureFlagGuard;
};
